import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { AuthApiService } from '../../../services/auth-api.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-reset-password-otp',
  imports: [CommonModule, FormsModule, RouterLink, TranslateModule],
  templateUrl: './reset-password-otp.component.html',
  styleUrl: './reset-password-otp.component.css'
})
export class ResetPasswordOtpComponent {

  otp = ['', '', '', '', '', ''];
  loading = false;
  resetLoading = false;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authApiService = inject(AuthApiService);

  languageService = inject(LanguageService);
  apiErrorService = inject(ApiErrorService);
  toastNotificationService = inject(ToastNotificationService);
  translate = inject(TranslateService);

  @ViewChildren('otpInput')
  otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // ngOnInit() {
  //   this.route.queryParams.subscribe(params => {
  //     this.email = params['email'] || '';
  //   });
  // }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;

    const value = input.value.replace(/\D/g, '').slice(0, 1);

    input.value = value;
    this.otp[index] = value;

    if (value && index < 5) {
      requestAnimationFrame(() => {
        this.otpInputs.toArray()[index + 1]?.nativeElement.focus();
      });
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const inputs = this.otpInputs.toArray();
    pasted.split('').forEach((digit, index) => {
      this.otp[index] = digit;
      inputs[index].nativeElement.value = digit;
    });
    inputs[Math.min(pasted.length - 1, 5)]?.nativeElement.focus();
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && index > 0) {
      this.otpInputs.toArray()[index - 1]?.nativeElement.focus();
    }
  }

  resendOtp(): void {
    const email = sessionStorage.getItem('email_to_reset');

    if (!email) {
      this.toastNotificationService.createNotification(
        this.translate.instant('auth.reset_password.toast_error_message'),
        ToastNotificationType.ERROR,
        this.translate.instant('auth.reset_password.toast_error_title'),
        ToastPosition.TOP_RIGHT
      );

      this.router.navigate(['/reset-password']);
      return;
    }

  this.resetLoading = true;

  this.authApiService.requestPasswordResetOtp({ email })
    .pipe(
      finalize(() => this.resetLoading = false)
    )
    .subscribe({
      next: () => {
        this.toastNotificationService.createNotification(
          this.translate.instant('auth.reset_password_otp.resend_success_message'),
          ToastNotificationType.SUCCESS,
          this.translate.instant('auth.reset_password_otp.resend_success_title'),
          ToastPosition.TOP_RIGHT
        );
      },
      error: () => {
        this.toastNotificationService.createNotification(
          this.translate.instant('auth.reset_password_otp.resend_error_message'),
          ToastNotificationType.ERROR,
          this.translate.instant('auth.reset_password_otp.resend_error_title'),
          ToastPosition.TOP_RIGHT
        );
      }
    });
  }

submit(): void {
  const otp = this.otp.join('');

  if (otp.length !== 6) {
    this.toastNotificationService.createNotification(
      this.translate.instant('auth.reset_password_otp.incomplete_code'),
      ToastNotificationType.ERROR,
      '',
      ToastPosition.TOP_RIGHT
    );
    return;
  }

  const email = sessionStorage.getItem("email_to_reset");

  if (!email) {
    this.toastNotificationService.createNotification(
      this.translate.instant('auth.reset_password_otp.no_email_found'),
      ToastNotificationType.ERROR,
      '',
      ToastPosition.TOP_RIGHT
    );
    this.router.navigate(['/reset-password']);
    return;
  }

  this.loading = true;

  this.authApiService.verifyPasswordResetOtp({
    email: email,
    otp
  }).subscribe({
    next: (response) => {
      let token = response.reset_token;
      sessionStorage.setItem("reset_token" , token);
      this.toastNotificationService.createNotification(
        this.translate.instant('auth.reset_password_otp.toast_success_message'),
        ToastNotificationType.SUCCESS,
        this.translate.instant('auth.reset_password_otp.toast_success_title'),
        ToastPosition.TOP_RIGHT
      );
      this.loading = false;
      this.router.navigate(['/new-password']);
    },
    error: (error) => {
      this.loading = false;
      this.toastNotificationService.createNotification(
        this.apiErrorService.getDisplayMessage(error, this.translate.instant('auth.reset_password_otp.toast_error_message')),
        ToastNotificationType.ERROR,
        this.translate.instant('auth.reset_password_otp.toast_error_title'),
        ToastPosition.TOP_RIGHT
      );

    }
  });
}

}
